import React from 'react';
import { Button } from '../ui/button';
import {
  Search as SearchIcon, Building2, Key, Briefcase, TrendingUp, ShieldCheck, ArrowLeft,
} from 'lucide-react';

// Base card order. We intentionally keep this immutable — A/B variants
// derive a reordered copy at render time so analytics can attribute the
// uplift cleanly. India funnel data: NRIs convert ~3× higher when
// "Investor / NRI" is the first card they see, so for IN traffic we
// surface that card first.
export const ROLE_CARDS = [
  { id: 'buyer',        label: 'Buyer',          tagline: 'Browse direct-from-seller listings + AI matches',     icon: <SearchIcon className="h-5 w-5" />, color: '#E07A3A' },
  { id: 'tenant',       label: 'Tenant',         tagline: 'Rent direct from verified landlords · no admin fees', icon: <Key className="h-5 w-5" />,        color: '#22C55E' },
  { id: 'seller',       label: 'Seller',         tagline: 'List free · pay only when a verified buyer matches',  icon: <Building2 className="h-5 w-5" />,  color: '#3B82F6' },
  { id: 'agent',        label: 'Estate Agent',   tagline: 'Get warm intros · earn 20% on every referral',        icon: <Briefcase className="h-5 w-5" />,  color: '#8B5CF6' },
  { id: 'professional', label: 'Professional',   tagline: 'Solicitor · Mortgage broker · Property manager',      icon: <ShieldCheck className="h-5 w-5" />, color: '#0EA5E9' },
  { id: 'investor',     label: 'Investor / NRI', tagline: 'Yield-first search across UK · India · Canada',       icon: <TrendingUp className="h-5 w-5" />, color: '#F59E0B' },
];

/** Returns a copy of ROLE_CARDS ordered for the given country. */
export function getRoleCardsForCountry(countryCode) {
  const cc = (countryCode || '').toUpperCase();
  if (cc === 'IN') {
    const cards = [...ROLE_CARDS];
    const investorIdx = cards.findIndex((c) => c.id === 'investor');
    const buyerIdx = cards.findIndex((c) => c.id === 'buyer');
    if (investorIdx > -1 && buyerIdx > -1 && investorIdx !== buyerIdx) {
      [cards[investorIdx], cards[buyerIdx]] = [cards[buyerIdx], cards[investorIdx]];
    }
    return cards;
  }
  return ROLE_CARDS;
}

/**
 * Two-step register flow — step 1: role picker. Each card is intentionally
 * short (1 verb + 1 outcome) so the page reads in 5 seconds. Data-testids
 * preserved from the legacy panel: role-picker-grid, role-card-{id},
 * role-picker-continue, role-picker-back.
 */
export const RolePicker = ({ role, onRoleChange, onContinue, countryCode }) => {
  const cards = getRoleCardsForCountry(countryCode);
  return (
    <div className="mb-6" data-testid="role-picker-grid">
      <div className="grid grid-cols-2 gap-2.5">
        {cards.map((r) => {
          const selected = r.id === role;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onRoleChange(r.id)}
              className={`text-left p-3.5 rounded-lg border-2 transition-all ${
                selected
                  ? 'bg-white shadow-md'
                  : 'bg-white/70 border-slate-200 hover:border-slate-300'
              }`}
              style={
                selected
                  ? { borderColor: r.color, boxShadow: `0 6px 18px -8px ${r.color}80` }
                  : undefined
              }
              data-testid={`role-card-${r.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-7 h-7 rounded flex items-center justify-center"
                  style={{ backgroundColor: `${r.color}1A`, color: r.color }}
                >
                  {r.icon}
                </span>
                <span className="font-bold text-sm text-[#0F172A]">{r.label}</span>
              </div>
              <p className="text-[11px] text-slate-500 leading-snug">{r.tagline}</p>
            </button>
          );
        })}
      </div>
      <Button
        onClick={onContinue}
        className="w-full mt-4 bg-[#0F172A] hover:bg-[#1F2937] text-white py-5 font-semibold tracking-wider uppercase text-xs"
        data-testid="role-picker-continue"
      >
        Continue as {ROLE_CARDS.find((c) => c.id === role)?.label} →
      </Button>
      <p className="text-[11px] text-slate-400 text-center mt-3">
        You can change role later from your profile.
      </p>
    </div>
  );
};

export const RolePickerBackLink = ({ role, onBack }) => (
  <button
    onClick={onBack}
    className="mb-3 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors"
    data-testid="role-picker-back"
  >
    <ArrowLeft className="h-3 w-3" />
    Signing up as{' '}
    <span className="font-bold text-[#0F172A]">
      {ROLE_CARDS.find((c) => c.id === role)?.label}
    </span>{' '}
    · change
  </button>
);

export default RolePicker;
